import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { productImage } from "@/lib/api";
import StorefrontLayout from "@/components/StorefrontLayout";
import AIChat from "@/components/AIChat";

const DEFAULT_CATS = ["All", "Print", "Digital", "Apparel", "Audiobooks", "Wallpapers", "Templates", "Presets", "Prompt Packs", "Health Guides", "Life Guides", "AI Skills Guides"];

const AUDIENCE_TO_TAG = {
  "real-estate":    { tag: "real estate",     label: "For Real Estate" },
  "creators":       { tag: "content creators", label: "For Content Creators" },
  "saas":           { tag: "saas founders",   label: "For SaaS Founders" },
  "small-business": { tag: "small business",  label: "For Small Business" },
};

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "All";
  const q = params.get("q") || "";
  const audience = params.get("audience") || "";
  const audienceInfo = AUDIENCE_TO_TAG[audience] || null;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState(DEFAULT_CATS);

  useEffect(() => {
    api.get("/categories").then((r) => {
      if (r.data?.categories) setCats(["All", ...r.data.categories]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { category: cat, q };
    if (audienceInfo) params.tag = audienceInfo.tag;
    api.get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [cat, q, audienceInfo]);

  return (
    <StorefrontLayout>
      <section className="rule-b">
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-12 sm:py-16">
          <div className="label-mono mb-3">/ SHOP {audienceInfo && `— ${audienceInfo.label.toUpperCase()}`}{q && ` — "${q}"`}</div>
          <h1 className="font-display text-5xl sm:text-7xl font-medium tracking-tight">{audienceInfo ? audienceInfo.label : "The collection."}</h1>
          <p className="mt-4 text-lg max-w-xl" style={{ color: "var(--ink-soft)" }}>
            All editions, browsable. Free shipping over $50, signed and numbered when noted.
          </p>
          <div className="mt-8 flex gap-2 flex-wrap">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`cat-${c}`}
                onClick={() => { params.set("category", c); setParams(params); }}
                className="px-4 py-2 rounded-full text-sm font-medium transition border"
                style={{
                  background: cat === c ? "var(--ink)" : "transparent",
                  color: cat === c ? "var(--paper)" : "var(--ink)",
                  borderColor: "var(--ink)",
                }}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1320px] mx-auto px-6 sm:px-10 py-12">
          {loading && <div className="font-mono-ui text-sm">Loading…</div>}
          {!loading && products.length === 0 && (
            <div className="bx p-10 text-center">
              <div className="font-display text-2xl">Nothing here yet.</div>
              <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>Try another category or search term.</p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((p, idx) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                data-testid={`product-card-${p.id}`}
                className={`product-card group block rise rise-${(idx % 4) + 1}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bx" style={{ background: "var(--paper-2)" }}>
                  <img src={productImage(p)} alt={p.title} className="w-full h-full object-cover transition duration-700 group-hover:scale-[1.04]" />
                  <div className="product-overlay" />
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-medium truncate">{p.title}</div>
                    <div className="label-mono mt-1">{p.category}{p.ai_generated && " · STUDIO ED."}</div>
                  </div>
                  <div className="font-mono-ui text-sm mt-1">${p.price.toFixed(2)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AIChat />
    </StorefrontLayout>
  );
}
